import { useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { Activity, CheckCircle2, ChevronRight, Loader, Play, XCircle, Zap } from 'lucide-react-native';

import { Badge, Button, Card, StatTile } from '@/components/ui';
import { buildTests, runTest, type TestRun, type TestStep, ENV_INFO } from '@/services/diagnostico';
import { ENV_LABELS, env } from '@/config/env';
import { colors, radius, typography } from '@/theme/tokens';

export function DiagnosticoScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const tests = useMemo(() => buildTests(), []);
  const [runs, setRuns] = useState<Record<string, TestRun>>({});
  const [running, setRunning] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map<string, TestStep[]>();
    tests.forEach((t) => {
      const arr = map.get(t.grupo) ?? [];
      arr.push(t);
      map.set(t.grupo, arr);
    });
    return Array.from(map.entries());
  }, [tests]);

  const runOne = async (t: TestStep) => {
    setRuns((p) => ({ ...p, [t.id]: { id: t.id, status: 'running' } }));
    const result = await runTest(t);
    setRuns((p) => ({ ...p, [t.id]: result }));
  };

  const runAll = async () => {
    setRunning(true);
    setRuns({});
    for (const t of tests) {
      setRuns((p) => ({ ...p, [t.id]: { id: t.id, status: 'running' } }));
      const result = await runTest(t);
      setRuns((p) => ({ ...p, [t.id]: result }));
    }
    setRunning(false);
  };

  const stats = useMemo(() => {
    const all = Object.values(runs);
    return {
      total: tests.length,
      ejecutados: all.length,
      pasaron: all.filter((r) => r.status === 'pass').length,
      fallaron: all.filter((r) => r.status === 'fail').length,
      msTotal: all.reduce((a, r) => a + (r.ms ?? 0), 0),
    };
  }, [runs, tests.length]);

  const envCfg = ENV_LABELS[env];
  const info = ENV_INFO();

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 20, paddingBottom: 80 }}>
      {/* Hero del ambiente */}
      <Card padding={24} style={{ gap: 14 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
          <View
            style={{
              width: 52,
              height: 52,
              borderRadius: 18,
              backgroundColor: envCfg.bgColor,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Activity size={24} color={envCfg.color} />
          </View>
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Text
                style={{
                  fontFamily: typography.display.fontFamily,
                  fontSize: 24,
                  fontWeight: typography.weights.extrabold,
                  color: colors.ink[900],
                  letterSpacing: -0.5,
                }}
              >
                Diagnóstico del sistema
              </Text>
              <Badge label={envCfg.label} tone={env === 'production' ? 'success' : env === 'staging' ? 'warning' : 'info'} />
            </View>
            <Text style={{ color: colors.ink[500], fontSize: 13, marginTop: 4 }}>
              Proyecto Firebase · {info.proyecto}
            </Text>
          </View>
          <Button
            label={running ? 'Ejecutando…' : 'Ejecutar todo'}
            iconLeft={running ? <Loader size={16} color={colors.white} /> : <Play size={16} color={colors.white} />}
            onPress={runAll}
            loading={running}
          />
        </View>

        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
          <StatTile label="Total tests" value={String(stats.total)} accent="magenta" icon={<Activity size={20} color={colors.magenta[700]} />} />
          <StatTile label="Pasaron" value={String(stats.pasaron)} accent="success" icon={<CheckCircle2 size={20} color={colors.success[600]} />} />
          <StatTile label="Fallaron" value={String(stats.fallaron)} accent="danger" icon={<XCircle size={20} color={colors.danger[600]} />} />
          <StatTile label="Tiempo total" value={`${stats.msTotal} ms`} accent="neutral" icon={<Zap size={20} color={colors.ink[600]} />} />
        </View>
      </Card>

      {/* Tests por grupo */}
      {grouped.map(([grupo, items]) => (
        <Card key={grupo} style={{ gap: 10 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
              {grupo}
            </Text>
            <Badge label={`${items.length} tests`} tone="neutral" />
          </View>
          <View style={{ gap: 8 }}>
            {items.map((t) => (
              <TestRow key={t.id} test={t} run={runs[t.id]} onRun={() => runOne(t)} />
            ))}
          </View>
        </Card>
      ))}
    </ScrollView>
  );
}

function TestRow({ test, run, onRun }: { test: TestStep; run: TestRun | undefined; onRun: () => void }) {
  const status = run?.status ?? 'idle';

  const tone =
    status === 'pass' ? colors.success : status === 'fail' ? colors.danger : status === 'running' ? colors.magenta : colors.ink;

  const Icon =
    status === 'pass' ? CheckCircle2 : status === 'fail' ? XCircle : status === 'running' ? Loader : ChevronRight;

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
        padding: 14,
        borderRadius: radius['2xl'],
        backgroundColor:
          status === 'pass'
            ? colors.success[50]
            : status === 'fail'
              ? colors.danger[50]
              : status === 'running'
                ? colors.magenta[50]
                : colors.crema[50],
        borderWidth: 1,
        borderColor:
          status === 'pass'
            ? '#B7E2C7'
            : status === 'fail'
              ? '#FBD0D0'
              : status === 'running'
                ? colors.magenta[200]
                : colors.ink[100],
      }}
    >
      <View
        style={{
          width: 36,
          height: 36,
          borderRadius: 12,
          backgroundColor: status === 'idle' ? colors.white : tone[50],
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={18} color={tone[600]} />
      </View>
      <View style={{ flex: 1, gap: 4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Text style={{ fontWeight: typography.weights.bold, color: colors.ink[900], fontSize: 14 }}>{test.titulo}</Text>
          {run?.ms !== undefined ? (
            <Text style={{ fontSize: 11, color: colors.ink[500], fontFamily: 'monospace' }}>{run.ms} ms</Text>
          ) : null}
        </View>
        <Text style={{ fontSize: 12, color: colors.ink[500] }}>{test.descripcion}</Text>
        {run?.detalle ? (
          <Text style={{ fontSize: 12, color: colors.success[600], fontWeight: typography.weights.semibold }}>
            ✓ {run.detalle}
          </Text>
        ) : null}
        {run?.error ? (
          <Text style={{ fontSize: 12, color: colors.danger[600], fontWeight: typography.weights.semibold }}>
            ✗ {run.error}
          </Text>
        ) : null}
      </View>
      <Pressable
        onPress={onRun}
        disabled={status === 'running'}
        style={({ hovered }) => ({
          paddingHorizontal: 10,
          height: 30,
          borderRadius: 10,
          backgroundColor: hovered ? colors.magenta[100] : colors.white,
          borderWidth: 1,
          borderColor: colors.magenta[200],
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'row',
          gap: 6,
          opacity: status === 'running' ? 0.5 : 1,
          // @ts-ignore web only
          cursor: status === 'running' ? 'not-allowed' : 'pointer',
        })}
      >
        <Play size={11} color={colors.magenta[700]} />
        <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.magenta[700] }}>Run</Text>
      </Pressable>
    </View>
  );
}
